// src/components/learning/CarnetProgression.js
// Carnet de progression de l'élève — lettres maîtrisées niveau Abjad
// Intégré dans Dashboard.js comme onglet "Ma progression"

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Target, Trophy } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;

// 28 lettres dans l'ordre alphabétique
const LETTRES_ABJAD = [
  { lettre: 'ا', nom: 'الأَلِف', transliteration: 'Alif' },
  { lettre: 'ب', nom: 'الباء', transliteration: 'Ba' },
  { lettre: 'ت', nom: 'التاء', transliteration: 'Ta' },
  { lettre: 'ث', nom: 'الثاء', transliteration: 'Tha' },
  { lettre: 'ج', nom: 'الجيم', transliteration: 'Jim' },
  { lettre: 'ح', nom: 'الحاء', transliteration: 'Ha' },
  { lettre: 'خ', nom: 'الخاء', transliteration: 'Kha' },
  { lettre: 'د', nom: 'الدال', transliteration: 'Dal' },
  { lettre: 'ذ', nom: 'الذال', transliteration: 'Dhal' },
  { lettre: 'ر', nom: 'الراء', transliteration: 'Ra' },
  { lettre: 'ز', nom: 'الزاي', transliteration: 'Zayn' },
  { lettre: 'س', nom: 'السين', transliteration: 'Sin' },
  { lettre: 'ش', nom: 'الشين', transliteration: 'Shin' },
  { lettre: 'ص', nom: 'الصاد', transliteration: 'Sad' },
  { lettre: 'ض', nom: 'الضاد', transliteration: 'Dad' },
  { lettre: 'ط', nom: 'الطاء', transliteration: 'Ta emp.' },
  { lettre: 'ظ', nom: 'الظاء', transliteration: 'Dha emp.' },
  { lettre: 'ع', nom: 'العين', transliteration: 'Ayn' },
  { lettre: 'غ', nom: 'الغين', transliteration: 'Ghayn' },
  { lettre: 'ف', nom: 'الفاء', transliteration: 'Fa' },
  { lettre: 'ق', nom: 'القاف', transliteration: 'Qaf' },
  { lettre: 'ك', nom: 'الكاف', transliteration: 'Kaf' },
  { lettre: 'ل', nom: 'اللام', transliteration: 'Lam' },
  { lettre: 'م', nom: 'الميم', transliteration: 'Mim' },
  { lettre: 'ن', nom: 'النون', transliteration: 'Nun' },
  { lettre: 'ه', nom: 'الهاء', transliteration: 'Ha' },
  { lettre: 'و', nom: 'الواو', transliteration: 'Waw' },
  { lettre: 'ي', nom: 'الياء', transliteration: 'Ya' },
];

const statutConfig = {
  maitrisee:   { label: 'Maîtrisée' },
  en_cours:    { label: 'En cours' },
  vue:         { label: 'Vue' },
  non_abordee: { label: 'À faire' },
};

// Couleurs des pastilles selon le statut (vert = acquise, or = en cours, gris = à faire)
const chipStyle = (s) =>
  s === 'maitrisee' ? { background: '#059669', color: '#fff',     border: '2px solid #059669' }
: s === 'en_cours'  ? { background: '#ffffff', color: '#8a6a33', border: '2px solid #c99a5b' }
: s === 'vue'       ? { background: '#d1fae5', color: '#065f46', border: '2px solid #d1fae5' }
:                     { background: '#f1efe9', color: '#b9b6ac', border: '2px solid #f1efe9' };

const pill = (active) => ({
  borderRadius: 20, padding: '7px 14px', fontSize: 12.5, fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 6,
  background: active ? '#059669' : '#f1efe9',
  color: active ? '#fff' : '#9aa79f',
});

export default function CarnetProgression({ token }) {
  const [progression, setProgression] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/progression/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      if (res.data.success) {
        setProgression(res.data.progression || []);
        setStats(res.data.stats);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const lettresAvecStatut = LETTRES_ABJAD.map(l => {
    const prog = progression.find(p => p.lettre === l.lettre);
    let statut = 'non_abordee';
    if (prog) {
      if (prog.statut === 'maitrisee') statut = 'maitrisee';
      else if (prog.statut === 'en_cours') statut = 'en_cours';
      else if (prog.lettre_vue) statut = 'vue';
    }
    return { ...l, statut, prog };
  });

  const pct = stats ? stats.pourcentage : 0;
  const prochaine = lettresAvecStatut.find(l => l.statut === 'en_cours')
                 || lettresAvecStatut.find(l => l.statut === 'non_abordee');

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
      Chargement de votre progression…
    </div>
  );

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* ── NIVEAUX ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span style={pill(true)}>Niveau 1 · Lettres</span>
        <span style={pill(false)}><Lock size={13} strokeWidth={2} />Niveau 2 · Mots</span>
        <span style={pill(false)}><Lock size={13} strokeWidth={2} />Niveau 3 · Phrases</span>
      </div>

      {/* ── PROGRESSION NIVEAU 1 ── */}
      <div style={{ background: '#fff', border: '1px solid #e6e9e4', borderRadius: 12, padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: '#6b7c74' }}>
            Niveau 1 — lettres · {stats?.maitrisees || 0} / 28 acquises
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{pct}%</div>
        </div>
        <div style={{ height: 8, borderRadius: 6, background: '#e6e9e4', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: '#059669', borderRadius: 6, transition: 'width 0.8s ease' }} />
        </div>

        {/* Alphabet compact — une seule ligne (défilement horizontal si l'écran est étroit) */}
        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, marginTop: 14, overflowX: 'auto', paddingBottom: 4 }}>
          {lettresAvecStatut.map(l => (
            <div key={l.lettre}
              title={`${l.transliteration} — ${statutConfig[l.statut].label}`}
              style={{
                flex: '0 0 auto', width: 28, height: 28, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: "'Amiri', 'Traditional Arabic', serif", fontSize: 16,
                direction: 'rtl', lineHeight: 1, ...chipStyle(l.statut),
              }}>
              {l.lettre}
            </div>
          ))}
        </div>

        {/* Prochaine à évaluer (repère pour le prof) */}
        {prochaine && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, color: '#6b7c74' }}>
            <Target size={16} strokeWidth={2} color="#c99a5b" />
            À évaluer / en cours :
            <b style={{ fontFamily: "'Amiri', serif", fontSize: 18 }}>{prochaine.lettre}</b>
            <span style={{ color: '#9aa79f' }}>({prochaine.transliteration})</span>
          </div>
        )}

        {/* Légende */}
        <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 11.5, color: '#9aa79f', flexWrap: 'wrap' }}>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#059669', marginRight: 5 }} />acquise</span>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#c99a5b', marginRight: 5 }} />en cours</span>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 2, background: '#e6e9e4', marginRight: 5 }} />à faire</span>
        </div>
      </div>

      {/* ── MESSAGES ── */}
      {stats && stats.maitrisees === 0 && (
        <div style={{ textAlign: 'center', padding: 20, background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#065f46', marginBottom: 4 }}>Ton voyage commence ici</div>
          <div style={{ fontSize: 13, color: '#047857' }}>Réserve ton premier cours pour commencer à apprendre les lettres arabes.</div>
        </div>
      )}
      {stats && stats.maitrisees === 28 && (
        <div style={{ textAlign: 'center', padding: 20, background: '#fef7e6', borderRadius: 12, border: '1px solid #f0e2cd' }}>
          <Trophy size={30} strokeWidth={1.8} color="#c99a5b" style={{ marginBottom: 6 }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: '#8a6a33', marginBottom: 4 }}>Niveau 1 complété !</div>
          <div style={{ fontSize: 13, color: '#a8792f' }}>Tu maîtrises les 28 lettres. Place aux mots — Niveau 2 !</div>
        </div>
      )}
    </div>
  );
}
